import { useEffect, useState, useCallback } from "react";
import { getWsHealth, type WsHealth } from "../api/client";
import { formatDistanceToNow } from "date-fns";

const POLL_INTERVAL_MS = 30_000; // poll every 30 000 ms (30 s)

export default function WsHealthMonitor() {
  const [health, setHealth] = useState<WsHealth | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWsHealth();
      setHealth(res.data);
      setLastUpdated(new Date());
    } catch {
      // Silently ignore (e.g. FINNHUB_API_KEY not configured)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  if (!health) return null;

  const connected = health.ws_connected;
  const symbolEntries = Object.entries(health.symbols);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: "14px 18px",
        marginBottom: 20,
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: symbolEntries.length > 0 ? 12 : 0,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: connected ? "#22c55e" : "#ef4444",
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
          WebSocket {connected ? "Connected" : "Disconnected"}
        </span>
        <button
          onClick={refresh}
          disabled={loading}
          title="Refresh health status"
          style={{
            marginLeft: "auto",
            background: "none",
            border: "1px solid #e2e8f0",
            borderRadius: 5,
            padding: "2px 8px",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 12,
            color: "#64748b",
          }}
        >
          {loading ? "…" : "↻"}
        </button>
        {lastUpdated && (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}
          </span>
        )}
      </div>

      {/* Per-symbol summary */}
      {symbolEntries.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {symbolEntries.map(([sym, info]) => {
            const hasActivity = info.ticks_5min > 0;
            return (
              <div
                key={sym}
                style={{
                  background: hasActivity ? "#f0fdf4" : "#f8fafc",
                  border: `1px solid ${hasActivity ? "#bbf7d0" : "#e2e8f0"}`,
                  borderRadius: 6,
                  padding: "6px 12px",
                  fontSize: 12,
                  minWidth: 120,
                }}
              >
                <div style={{ fontWeight: 700, fontFamily: "monospace", color: "#1e293b", marginBottom: 2 }}>
                  {sym}
                </div>
                <div style={{ color: hasActivity ? "#15803d" : "#94a3b8" }}>
                  {info.ticks_5min} tick{info.ticks_5min !== 1 ? "s" : ""} / 5 min
                </div>
                {info.last_tick_at && (
                  <div style={{ color: "#64748b", marginTop: 2 }}>
                    last: {formatDistanceToNow(new Date(info.last_tick_at), { addSuffix: true })}
                  </div>
                )}
                {!info.last_tick_at && (
                  <div style={{ color: "#94a3b8", marginTop: 2 }}>no data yet</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {symbolEntries.length === 0 && (
        <div style={{ fontSize: 12, color: "#94a3b8" }}>
          No subscriptions active. Subscribe to symbols to start monitoring.
        </div>
      )}
    </div>
  );
}
